import classnames from 'classnames';
import React, {useRef} from 'react';

const KEYBOARD_STEP = 2;

interface PaneResizeRailProps {
  label: string;
  /** The resized pane's share of the container, in percent. */
  share: number;
  min: number;
  max: number;
  /** Which side of the rail the resized pane sits on; dragging away from it grows it. */
  paneSide: 'start' | 'end';
  /** The element whose width the share is a percentage of. */
  containerRef: React.RefObject<HTMLElement | null>;
  isDragging: boolean;
  onDraggingChange: (isDragging: boolean) => void;
  onResize: (share: number) => void;
  /** Called on double-click: returns the pane to its default width. */
  onReset: () => void;
  className?: string;
}

/**
 * A draggable divider between two panes, styled after the adaptive practice split-view resizer.
 * The 24px hit area straddles its grid track; its centered hairline is the only visible divider.
 */
const PaneResizeRail = ({
  label,
  share,
  min,
  max,
  paneSide,
  containerRef,
  isDragging,
  onDraggingChange,
  onResize,
  onReset,
  className
}: PaneResizeRailProps) => {
  const dragStart = useRef<{x: number; share: number; width: number} | null>(null);
  const growDirection = paneSide === 'start' ? 1 : -1;

  const endDrag = () => {
    if (!dragStart.current) return;
    dragStart.current = null;
    onDraggingChange(false);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const width = containerRef.current?.getBoundingClientRect().width;
    if (event.button !== 0 || !width) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragStart.current = {x: event.clientX, share, width};
    onDraggingChange(true);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = dragStart.current;
    if (!start) return;
    const delta = ((event.clientX - start.x) / start.width) * 100;
    onResize(start.share + delta * growDirection);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const steps = {ArrowRight: 1, ArrowLeft: -1}[event.key];
    if (steps === undefined) return;
    event.preventDefault();
    onResize(share + steps * growDirection * KEYBOARD_STEP);
  };

  return (
    <div
      role='separator'
      aria-orientation='vertical'
      aria-label={label}
      aria-valuenow={Math.round(share)}
      aria-valuemin={Math.round(min)}
      aria-valuemax={Math.round(max)}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onLostPointerCapture={endDrag}
      onDoubleClick={onReset}
      onKeyDown={handleKeyDown}
      className={classnames(
        'group -mx-x1-half w-x3 relative z-1 cursor-ew-resize touch-none outline-none',
        className
      )}
    >
      <span
        aria-hidden
        className={classnames(
          'absolute inset-y-x0 left-1/2 -translate-x-1/2 border-0 border-l border-solid group-focus-visible:border-l-4 group-focus-visible:border-selected',
          isDragging
            ? 'border-l-4 border-selected'
            : 'border-subtle group-hover:border-l-4 group-hover:border-bold'
        )}
      />
    </div>
  );
};

export default PaneResizeRail;
